/**
 * useVoiceOutput — wraps the browser's SpeechSynthesis.
 *
 * Phase 2A uses the OS voices (Windows + Mac + iOS ship with Arabic voices,
 * though quality varies). Phase 2B will pre-fetch Azure ar-IQ-RanaNeural mp3
 * from the server and play that instead — same callback contract.
 */
import { useCallback, useEffect, useState } from "react";

export function useVoiceOutput() {
	const supported =
		typeof window !== "undefined" && "speechSynthesis" in window;

	const [speaking, setSpeaking] = useState(false);
	const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

	useEffect(() => {
		if (!supported) return;
		const update = () => setVoices(window.speechSynthesis.getVoices());
		update();
		window.speechSynthesis.addEventListener("voiceschanged", update);
		return () =>
			window.speechSynthesis.removeEventListener("voiceschanged", update);
	}, [supported]);

	/** Pick the best available voice for a BCP-47 language tag. */
	const pickVoice = useCallback(
		(lang: string): SpeechSynthesisVoice | null => {
			if (voices.length === 0) return null;
			const exact = voices.find(
				(v) => v.lang.toLowerCase() === lang.toLowerCase(),
			);
			if (exact) return exact;
			const prefix = lang.split("-")[0]?.toLowerCase();
			if (!prefix) return null;
			const prefixMatch = voices.find((v) =>
				v.lang.toLowerCase().startsWith(prefix),
			);
			return prefixMatch ?? null;
		},
		[voices],
	);

	const speak = useCallback(
		(text: string, lang: string) => {
			if (!supported || !text.trim()) return;
			window.speechSynthesis.cancel();
			const u = new SpeechSynthesisUtterance(text);
			u.lang = lang;
			const voice = pickVoice(lang);
			if (voice) u.voice = voice;
			u.rate = 1.0;
			u.pitch = 1.0;
			u.onstart = () => setSpeaking(true);
			u.onend = () => setSpeaking(false);
			u.onerror = () => setSpeaking(false);
			window.speechSynthesis.speak(u);
		},
		[supported, pickVoice],
	);

	const stop = useCallback(() => {
		if (!supported) return;
		window.speechSynthesis.cancel();
		setSpeaking(false);
	}, [supported]);

	return { supported, speak, stop, speaking, voices };
}
